class ProductBundle {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.config = window[`bundleConfig_${sectionId.replace(/-/g, '_')}`];
    
    if (!this.config) {
      console.error('Bundle config not found');
      return;
    }

    this.bundleItems = [];
    this.productQuantities = {};
    this.init();
  }

  init() {
    this.container = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    if (!this.container) return;

    this.elements = {
      addButtons: this.container.querySelectorAll('[data-bundle-add]'),
      bundleItemsContainer: this.container.querySelector('[data-bundle-items]'),
      emptyState: this.container.querySelector('[data-empty-state]'),
      submitButton: this.container.querySelector('[data-bundle-submit]'),
      totalPrice: this.container.querySelector('[data-bundle-total]'),
      savingsAmount: this.container.querySelector('[data-savings-amount]'),
      savingsRow: this.container.querySelector('[data-savings-row]'),
      messageContainer: this.container.querySelector('[data-bundle-message]'),
      bundleToggle: this.container.querySelector('[data-bundle-toggle]'),
      bundleSidebar: this.container.querySelector('.bundle-sidebar-sticky')
    };

    this.attachEventListeners();
  }

  attachEventListeners() {
    this.elements.addButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleAddToBundle(e));
    });

    this.elements.submitButton.addEventListener('click', () => this.submitBundle());

    if (this.elements.bundleToggle) {
      this.elements.bundleToggle.addEventListener('click', () => {
        this.elements.bundleSidebar.classList.toggle('expanded');
      });
    }

    this.container.addEventListener('click', (e) => {
      const plusBtn = e.target.closest('[data-qty-plus]');
      const minusBtn = e.target.closest('[data-qty-minus]');
      
      if (plusBtn) {
        const variantId = plusBtn.dataset.qtyPlus;
        this.updateProductQuantity(variantId, 1);
      } else if (minusBtn) {
        const variantId = minusBtn.dataset.qtyMinus;
        this.updateProductQuantity(variantId, -1);
      }
    });

    this.elements.bundleItemsContainer.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-bundle-remove]');
      const qtyPlus = e.target.closest('[data-bundle-qty-plus]');
      const qtyMinus = e.target.closest('[data-bundle-qty-minus]');
      
      if (removeBtn) {
        const variantId = removeBtn.dataset.variantId;
        this.removeFromBundle(variantId);
      } else if (qtyPlus) {
        const variantId = qtyPlus.dataset.variantId;
        this.updateBundleItemQuantity(variantId, 1);
      } else if (qtyMinus) {
        const variantId = qtyMinus.dataset.variantId;
        this.updateBundleItemQuantity(variantId, -1);
      }
    });
  }

  updateProductQuantity(variantId, change) {
    const qtyInput = this.container.querySelector(`[data-qty-input="${variantId}"]`);
    if (!qtyInput) return;

    let currentQty = parseInt(qtyInput.value) || 1;
    let newQty = Math.max(1, Math.min(10, currentQty + change));
    
    qtyInput.value = newQty;
    this.productQuantities[variantId] = newQty;

    const bundleItem = this.bundleItems.find(item => item.variantId === variantId);
    if (bundleItem) {
      bundleItem.quantity = newQty;
      this.updateBundleUI();
    }
  }

  updateBundleItemQuantity(variantId, change) {
    const item = this.bundleItems.find(item => item.variantId === variantId);
    
    if (item) {
      const newQty = Math.max(1, Math.min(10, item.quantity + change));
      item.quantity = newQty;
      this.productQuantities[variantId] = newQty;
      
      const qtyInput = this.container.querySelector(`[data-qty-input="${variantId}"]`);
      if (qtyInput) {
        qtyInput.value = newQty;
      }
      
      this.updateBundleUI();
    }
  }

  handleAddToBundle(e) {
    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    const quantity = this.productQuantities[variantId] || 1;
    
    const productData = {
      id: button.dataset.productId,
      variantId: variantId,
      title: button.dataset.productTitle,
      price: parseInt(button.dataset.productPrice),
      image: button.dataset.productImage,
      url: button.dataset.productUrl,
      quantity: quantity
    };

    const existingItem = this.bundleItems.find(item => item.variantId === variantId);
    
    if (existingItem) {
      this.showMessage('This item is already in your bundle', 'info');
      return;
    }

    if (this.bundleItems.length >= this.config.maximumItems) {
      this.showMessage('Maximum items reached', 'warning');
      return;
    }

    this.bundleItems.push(productData);
    this.updateBundleUI();
    this.showMessage('Item added to bundle', 'success');

    // Hide add button, show quantity controls
    button.style.display = 'none';
    const productActions = this.container.querySelector(`[data-product-actions="${variantId}"]`);
    if (productActions) {
      productActions.style.opacity = '1';
      productActions.style.height = 'auto';
      productActions.style.visibility = 'visible';
    }
  }

  removeFromBundle(variantId) {
    const index = this.bundleItems.findIndex(item => item.variantId === variantId);
    
    if (index > -1) {
      this.bundleItems.splice(index, 1);
      delete this.productQuantities[variantId];
      
      this.updateBundleUI();
      this.showMessage('Item removed from bundle', 'info');

      // Show add button, hide quantity controls
      const addButton = this.container.querySelector(`[data-variant-id="${variantId}"][data-bundle-add]`);
      if (addButton) {
        addButton.style.display = 'block';
      }
      
      const productActions = this.container.querySelector(`[data-product-actions="${variantId}"]`);
      if (productActions) {
        productActions.style.opacity = '0';
        productActions.style.height = '0';
        productActions.style.visibility = 'hidden';
      }
      
      const qtyInput = this.container.querySelector(`[data-qty-input="${variantId}"]`);
      if (qtyInput) {
        qtyInput.value = 1;
      }
    }
  }

  updateBundleUI() {
    if (this.bundleItems.length === 0) {
      this.elements.emptyState.style.display = 'block';
      this.elements.bundleItemsContainer.querySelectorAll('.bundle-item').forEach(el => el.remove());
    } else {
      this.elements.emptyState.style.display = 'none';
      this.renderBundleItems();
    }

    this.updatePricing();
    this.updateSubmitButton();
  }

  renderBundleItems() {
    this.elements.bundleItemsContainer.querySelectorAll('.bundle-item').forEach(el => el.remove());

    this.bundleItems.forEach(item => {
      const itemHTML = this.createBundleItemHTML(item);
      this.elements.bundleItemsContainer.insertAdjacentHTML('beforeend', itemHTML);
    });
  }

  createBundleItemHTML(item) {
    return `
      <div class="bundle-item" data-variant-id="${item.variantId}">
        <div class="bundle-item-image">
          <img src="${item.image}" alt="${item.title}" loading="lazy">
        </div>
        <div class="bundle-item-details">
          <div class="bundle-item-title">${item.title}</div>
          <div class="bundle-item-price">${this.formatMoney(item.price)}</div>
          <div class="bundle-item-qty-controls">
            <button type="button" 
                    class="bundle-qty-btn" 
                    data-bundle-qty-minus 
                    data-variant-id="${item.variantId}"
                    aria-label="Decrease quantity">
              −
            </button>
            <span class="bundle-item-qty">${item.quantity}</span>
            <button type="button" 
                    class="bundle-qty-btn" 
                    data-bundle-qty-plus 
                    data-variant-id="${item.variantId}"
                    aria-label="Increase quantity">
              +
            </button>
          </div>
        </div>
        <button type="button" 
                class="bundle-item-remove" 
                data-bundle-remove 
                data-variant-id="${item.variantId}"
                aria-label="Remove ${item.title}">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    `;
  }

  updatePricing() {
    const originalTotal = this.bundleItems.reduce((sum, item) => {
      return sum + (item.price * item.quantity);
    }, 0);

    let finalTotal = originalTotal;
    let savings = 0;

    if (this.bundleItems.length >= this.config.minimumItems) {
      if (this.config.discountType === 'percentage') {
        savings = originalTotal * (this.config.discountPercentage / 100);
        finalTotal = originalTotal - savings;
      } else if (this.config.discountType === 'fixed') {
        savings = this.config.discountAmount;
        finalTotal = Math.max(0, originalTotal - savings);
      }
    }

    this.elements.totalPrice.textContent = this.formatMoney(finalTotal);

    if (savings > 0) {
      this.elements.savingsAmount.textContent = this.formatMoney(savings);
      this.elements.savingsRow.style.display = 'block';
    } else {
      this.elements.savingsRow.style.display = 'none';
    }
  }

  updateSubmitButton() {
    const canSubmit = this.bundleItems.length >= this.config.minimumItems;
    this.elements.submitButton.disabled = !canSubmit;
  }

 async submitBundle() {
  if (this.bundleItems.length < this.config.minimumItems) {
    return;
  }

  this.elements.submitButton.disabled = true;
  this.elements.submitButton.textContent = 'Adding...';

  try {
    const items = this.bundleItems.map(item => ({
      id: item.variantId,
      quantity: item.quantity
    }));

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) throw new Error('Failed to add to cart');

    // Redirect to cart page
    window.location.href = '/cart';

  } catch (error) {
    console.error('Error adding bundle to cart:', error);
    this.showMessage('Error adding to cart', 'error');
    this.elements.submitButton.disabled = false;
    this.elements.submitButton.textContent = 'Add all to Cart';
  }
}

  showMessage(message, type = 'info') {
    const messageEl = this.elements.messageContainer;
    messageEl.textContent = message;
    messageEl.className = `bundle-message bundle-message--${type}`;
    messageEl.style.display = 'block';

    setTimeout(() => {
      messageEl.style.display = 'none';
    }, 3000);
  }

  formatMoney(cents) {
    const amount = cents / 100;
    
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, this.config.moneyFormat);
    }
    
    let format = this.config.moneyFormat || '{{amount}}';
    const formattedAmount = amount.toFixed(2);
    let result = format.replace('{{amount}}', formattedAmount);
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = result;
    result = tempDiv.textContent || tempDiv.innerText || result;
    
    return result;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.section-product-bundle').forEach(section => {
    const sectionId = section.querySelector('[data-section-id]')?.dataset.sectionId;
    if (sectionId) {
      new ProductBundle(sectionId);
    }
  });
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section.classList.contains('section-product-bundle')) {
      const sectionId = section.querySelector('[data-section-id]')?.dataset.sectionId;
      if (sectionId) {
        new ProductBundle(sectionId);
      }
    }
  });
}